import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { APP_NAME, APP_FULL, APP_TAGLINE, APP_FOOTER, APP_TAGLINE_SHORT, GPDP_LOGO, GPDP_LOGO_ALT } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Church, Mail, MapPin, Calendar as CalIcon, Megaphone, ArrowRight, Cross, Camera, Image as ImageIcon } from "lucide-react";

export default function PublicPortal() {
  const [churches, setChurches] = useState([]);
  const [events, setEvents] = useState([]);
  const [ann, setAnn] = useState([]);
  const [leadership, setLeadership] = useState([]);
  const [klasisProfile, setKlasisProfile] = useState(null);
  const [kegiatan, setKegiatan] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/public/churches").catch(()=>({data:[]})),
      api.get("/public/events").catch(()=>({data:[]})),
      api.get("/public/announcements").catch(()=>({data:[]})),
      api.get("/public/leadership").catch(()=>({data:[]})),
      api.get("/public/klasis-profile").catch(()=>({data:null})),
      api.get("/public/kegiatan?limit=6").catch(()=>({data:[]})),
    ]).then(([c,e,a,l,kp,k]) => {
      setChurches(c.data); setEvents(e.data); setAnn(a.data);
      setLeadership(l.data); setKlasisProfile(kp.data);
      setKegiatan(k.data);
    });
  }, []);

  const bpk = leadership.filter(p => p.organization === "BPK");
  const bp2k = leadership.filter(p => p.organization === "BP2K");
  const bagian = leadership.filter(p => ["PERIA","PERNITA","PEREDA","PERSEMI"].includes(p.organization));
  const bidang = leadership.filter(p => p.organization === "KOMISI");

  return (
    <div className="min-h-screen bg-background">
      {/* Nav — Official Portal Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/90 border-b border-border shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3 gap-4">
          <Link to="/public" className="flex items-center gap-3 min-w-0" data-testid="public-brand">
            <img src={GPDP_LOGO} alt={GPDP_LOGO_ALT} className="h-12 w-12 object-contain shrink-0" data-testid="public-brand-logo" />
            <div className="min-w-0 hidden sm:block">
              <div className="text-[0.65rem] tracking-[0.22em] uppercase text-secondary font-bold leading-tight">Gereja Pentakosta di Papua</div>
              <div className="font-heading font-black text-primary text-sm sm:text-base leading-tight">KLASIS MIMIKA</div>
              <div className="text-[0.6rem] text-muted-foreground tracking-widest uppercase leading-tight">SIMPELKAMI GPDP</div>
            </div>
            <div className="min-w-0 sm:hidden">
              <div className="font-heading font-black text-primary text-sm leading-tight">SIMPELKAMI GPDP</div>
              <div className="text-[0.55rem] text-muted-foreground tracking-widest uppercase">Klasis Mimika</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/public/statistik">
              <Button variant="ghost" size="sm" className="rounded-full text-primary hover:bg-primary/5 hidden sm:inline-flex" data-testid="public-stats-btn">Statistik Publik</Button>
            </Link>
            <Link to="/login">
              <Button size="sm" className="rounded-full shadow-sm" data-testid="public-login-btn">Masuk Sistem</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 -z-10">
          <img src="https://images.unsplash.com/photo-1671882865872-3f3740f95e77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxwYXB1YSUyMG5hdHVyZSUyMGZvcmVzdCUyMGxhbmRzY2FwZSUyMHN1bnJpc2V8ZW58MHx8fHwxNzg3OTcyNTI5fDA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/85 to-background" />
        </div>
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-4 mb-6">
                <img
                  src={GPDP_LOGO}
                  alt={GPDP_LOGO_ALT}
                  className="h-20 w-20 sm:h-24 sm:w-24 object-contain drop-shadow-[0_4px_16px_rgba(0,0,0,0.15)]"
                  data-testid="hero-brand-logo"
                />
                <div className="h-14 w-px bg-border" />
                <div className="text-xs tracking-[0.2em] uppercase text-secondary font-bold">GPDP Klasis Mimika<br/><span className="text-muted-foreground font-normal">Papua Tengah</span></div>
              </div>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground">
                {APP_NAME}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl">
                {APP_FULL}. Platform digital pelayanan, administrasi, data dan kolaborasi jemaat.
              </p>
              <div className="mt-6 flex items-center gap-4">
                <div className="h-1 w-16 bg-primary rounded-full" />
                <div className="h-1 w-4 bg-secondary rounded-full" />
                <div className="text-sm font-medium text-primary">{APP_TAGLINE}</div>
              </div>
            </div>
            <div className="lg:col-span-4">
              <Card className="border-primary/20 bg-card">
                <CardContent className="p-6">
                  <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Klasis Mimika</div>
                  <div className="mt-3 grid grid-cols-2 gap-4">
                    <div><div className="font-heading font-bold text-3xl text-primary">8</div><div className="text-xs text-muted-foreground">Jemaat</div></div>
                    <div><div className="font-heading font-bold text-3xl text-secondary">1</div><div className="text-xs text-muted-foreground">Klasis</div></div>
                  </div>
                  {klasisProfile?.periode_start && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="text-[0.6rem] tracking-[0.2em] uppercase text-muted-foreground">Periode BPK</div>
                      <div className="font-heading font-bold text-lg text-primary mt-0.5">
                        {new Date(klasisProfile.periode_start).getFullYear()} – {new Date(klasisProfile.periode_end).getFullYear()}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* BPK & BP2K Leadership */}
      {bpk.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-12">
          <div className="mb-8">
            <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Susunan Pengurus</div>
            <h2 className="font-heading font-bold text-3xl mt-1">Badan Pekerja Klasis (BPK)</h2>
            {klasisProfile?.sk_bpk && (
              <div className="text-xs text-muted-foreground mt-2">
                SK Nomor <span className="font-mono">{klasisProfile.sk_bpk}</span> • Periode {new Date(klasisProfile.periode_start).getFullYear()}–{new Date(klasisProfile.periode_end).getFullYear()}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {bpk.map((p) => (
              <Link key={p.id} to={`/kartu/${p.id}`} data-testid={`public-bpk-${p.id}`}>
                <Card className="hover:border-primary/30 hover:-translate-y-0.5 transition-transform h-full">
                  <CardContent className="p-5">
                    <div className="text-[0.65rem] tracking-[0.2em] uppercase text-secondary">{p.title}</div>
                    <div className="font-heading font-bold text-base mt-2 leading-snug">{p.person_name}</div>
                    <div className="text-[0.65rem] text-muted-foreground mt-2 hover:text-primary transition-colors">Lihat kartu →</div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {bagian.length > 0 && (
            <div className="mt-12">
              <h3 className="font-heading font-bold text-xl mb-4">Wadah Pelayanan</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {bagian.map((p) => (
                  <Card key={p.id} className="border-secondary/20 bg-secondary/5" data-testid={`public-bagian-${p.organization}`}>
                    <CardContent className="p-4">
                      <div className="text-[0.65rem] tracking-[0.2em] uppercase text-secondary">{p.organization}</div>
                      <div className="font-heading font-bold text-sm mt-1">{p.person_name}</div>
                      <div className="text-[0.65rem] text-muted-foreground mt-0.5">{p.title}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {bidang.length > 0 && (
            <div className="mt-12">
              <h3 className="font-heading font-bold text-xl mb-4">Bidang Klasis</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {bidang.map((p) => (
                  <Card key={p.id} className="border-accent/30 bg-accent/5" data-testid={`public-bidang-${p.id}`}>
                    <CardContent className="p-4">
                      <div className="text-[0.65rem] tracking-[0.2em] uppercase text-accent font-semibold">Bidang</div>
                      <div className="font-heading font-bold text-sm mt-1">{p.title.replace(/^Ketua\s+/, "")}</div>
                      <div className="text-xs text-muted-foreground mt-1">Ketua: <span className="font-medium">{p.person_name}</span></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {bp2k.length > 0 && (
            <div className="mt-12">
              <h3 className="font-heading font-bold text-xl mb-4">Badan Pengawas Perbendaharaan Klasis (BP2K)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {bp2k.map((p) => (
                  <Card key={p.id} className="border-primary/20" data-testid={`public-bp2k-${p.id}`}>
                    <CardContent className="p-4">
                      <div className="text-[0.65rem] tracking-[0.2em] uppercase text-primary">{p.title}</div>
                      <div className="font-heading font-bold text-sm mt-1">{p.person_name}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Jemaat */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Direktori</div>
            <h2 className="font-heading font-bold text-3xl mt-1">Jemaat Klasis Mimika</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {churches.map((c) => (
            <Card key={c.id} className="hover:border-primary/30 hover:-translate-y-0.5 transition-transform" data-testid={`public-church-${c.code}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center"><Cross className="h-5 w-5" /></div>
                  <div className="min-w-0">
                    <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">{c.code}</div>
                    <div className="font-heading font-bold text-base leading-tight mt-0.5">{c.name}</div>
                    {c.type === "POS_PI" && <div className="text-[0.65rem] text-secondary uppercase tracking-wide mt-1">Pos PI</div>}
                    <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
                      <MapPin className="h-3 w-3" /> {c.distrik || "Mimika"}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Events + Announcements */}
      <section className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-2 gap-8">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Agenda Publik</div>
          <h2 className="font-heading font-bold text-2xl mb-6">Kegiatan Terdekat</h2>
          <div className="space-y-3">
            {events.length === 0 && <div className="text-sm text-muted-foreground italic">Belum ada agenda publik.</div>}
            {events.map((e) => (
              <div key={e.id} className="flex items-start gap-4 border-b border-border pb-3">
                <div className="w-14 text-center shrink-0">
                  <div className="text-[0.65rem] uppercase text-muted-foreground">{new Date(e.date).toLocaleDateString('id-ID',{month:'short'})}</div>
                  <div className="font-heading font-bold text-2xl text-primary">{new Date(e.date).getDate()}</div>
                </div>
                <div>
                  <div className="font-medium">{e.title}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5"><MapPin className="h-3 w-3"/> {e.location || "—"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Pengumuman</div>
          <h2 className="font-heading font-bold text-2xl mb-6">Berita &amp; Pengumuman</h2>
          <div className="space-y-4">
            {ann.length === 0 && <div className="text-sm text-muted-foreground italic">Belum ada pengumuman.</div>}
            {ann.map((a) => (
              <Card key={a.id}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1"><Megaphone className="h-3.5 w-3.5 text-secondary" /><span className="text-[0.65rem] tracking-wide uppercase text-secondary">{a.priority || "Info"}</span></div>
                  <div className="font-heading font-bold">{a.title}</div>
                  <div className="text-sm text-muted-foreground mt-1">{a.body}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Kegiatan Gallery */}
      {kegiatan.length > 0 && (
        <section className="bg-muted/30 py-12 border-y border-border">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Dokumentasi</div>
                <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Camera className="h-6 w-6 text-secondary" /> Kegiatan Terkini</h2>
              </div>
              <div className="text-xs text-muted-foreground">{kegiatan.length} kegiatan publik</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {kegiatan.map((k) => (
                <Card key={k.id} className="overflow-hidden hover:border-primary/40 transition-colors">
                  <div className="h-40 bg-gradient-to-br from-primary/20 to-secondary/15 flex items-center justify-center relative">
                    {k.photo_count > 0 ? (
                      <div className="flex items-center gap-1 text-primary font-semibold">
                        <ImageIcon className="h-10 w-10" />
                        <span className="text-3xl">{k.photo_count}</span>
                      </div>
                    ) : (
                      <Camera className="h-12 w-12 text-muted-foreground" />
                    )}
                    <div className="absolute top-2 right-2 text-[0.6rem] tracking-widest uppercase bg-white/90 text-primary px-2 py-0.5 rounded-full font-bold">
                      {k.scope === "KLASIS" ? "BPK Klasis" : k.church_short_name || "Jemaat"}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="text-[0.65rem] uppercase tracking-widest text-secondary font-bold mb-1">{k.category}</div>
                    <div className="font-heading font-bold text-base leading-tight line-clamp-2 text-primary">{k.title}</div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <CalIcon className="h-3 w-3" /> {new Date(k.date).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })}
                    </div>
                    {k.location && <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 truncate"><MapPin className="h-3 w-3" /> {k.location}</div>}
                    {k.description && <p className="text-xs text-foreground/70 mt-2 line-clamp-2">{k.description}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact */}
      <section className="bg-primary text-primary-foreground py-14 mt-8">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase opacity-80 mb-3">Hubungi Klasis</div>
            <h2 className="font-heading font-bold text-3xl">Bersama membangun pelayanan yang tertib &amp; berdampak.</h2>
            <p className="mt-3 opacity-85 text-sm">{APP_TAGLINE}</p>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3"><Mail className="h-4 w-4" /> sekretariat@gpdp-mimika.org</div>
            <div className="flex items-center gap-3"><MapPin className="h-4 w-4" /> Timika, Kabupaten Mimika, Papua Tengah</div>
            <Link to="/login">
              <Button variant="outline" className="mt-3 rounded-full bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary" data-testid="public-login-cta">
                Masuk Sistem <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-8 text-xs text-muted-foreground text-center border-t border-border">
        {APP_FOOTER} • © {new Date().getFullYear()}
      </footer>
    </div>
  );
}
